import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Clock, Crown, ArrowLeft, Home, RefreshCw, Package, CreditCard, Calendar, AlertTriangle } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';
import { formatDateTime, extractBeijingTime } from '../utils/timeUtils';

interface OrderInfo {
  orderNo: string;
  planName: string;
  amount: number;
  originalPrice?: number;
  discountAmount?: number;
  pointsDeducted?: number;
  deductionAmount?: number;
  payMethod?: string;
  status: 'success' | 'fail' | 'pending';
  payTime?: string;
  expireDate?: string;
  packageInfo?: {
    organization: string;
    category: string;
    level_text: string;
    duration_type: string;
  };
}


function getDurationText(durationType: string): string {
  const durationMap: Record<string, string> = {
    'month': '1个月',
    'quarter': '3个月',
    'half_year': '6个月',
    'year': '1年',
  };
  return durationMap[durationType] || durationType;
}

export default function PaymentResult() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const status = searchParams.get('status') || 'success';
  const orderNo = searchParams.get('orderNo') || '';

  useEffect(() => {
    fetchOrderDetails();
  }, [orderNo]);

  // 当订单状态为 pending 时，自动轮询等待支付结果
  useEffect(() => {
    if (!order || order.status !== 'pending') return;

    let pollCount = 0;
    const maxPolls = 150; // 最多轮询5分钟
    const pollingRef = { current: null as ReturnType<typeof setTimeout> | null };
    const isPollingRef = { current: true };

    const poll = async () => {
      if (!isPollingRef.current) return;
      if (pollCount >= maxPolls) {
        isPollingRef.current = false;
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch(`${getSupabaseUrl()}/functions/v1/alipay-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: 'queryOrder', orderNo: order.orderNo }),
        });

        const result = await response.json();
        if (result.status === 'paid') {
          isPollingRef.current = false;
          fetchOrderDetails(); // 重新获取订单详情
          return;
        } else if (result.status === 'expired') {
          isPollingRef.current = false;
          setOrder(prev => prev ? { ...prev, status: 'fail' } : null);
          return;
        }
      } catch (err) {
        // 忽略错误，继续轮询
      }

      pollCount++;
      if (isPollingRef.current) {
        pollingRef.current = setTimeout(poll, 2000);
      }
    };

    poll();

    return () => {
      isPollingRef.current = false;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [order?.status, order?.orderNo]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setErrorMessage('请先登录');
        setLoading(false);
        return;
      }

      // 从统一 orders 表查询订单详情
      let foundOrder: any = null;

      let orderQuery = supabase
        .from('orders')
        .select('*')
        .eq('user_id', session.user.id);

      if (orderNo) {
        orderQuery = orderQuery.eq('order_no', orderNo);
      } else {
        orderQuery = orderQuery.order('created_at', { ascending: false });
      }

      const { data: ordersData, error: orderError } = await orderQuery.limit(1);

      if (orderError) {
        setErrorMessage('查询订单失败: ' + orderError.message);
        setLoading(false);
        return;
      }

      if (ordersData && ordersData.length > 0) {
        foundOrder = ordersData[0];
      }

      if (foundOrder) {
        const lastOrder = foundOrder;
        const expireDate = lastOrder.expire_date || (lastOrder.paid_at
          ? (() => {
              const bj = extractBeijingTime(lastOrder.paid_at);
              if (!bj) return '-';
              // 在北京时间基础上加30天
              const d = new Date(`${bj.year}-${bj.month}-${bj.day}T${bj.hour}:${bj.minute}:${bj.second}+08:00`);
              d.setDate(d.getDate() + 30);
              // 转回北京时间显示
              const bjResult = new Date(d.getTime() + 8 * 60 * 60 * 1000);
              return `${bjResult.getUTCFullYear()}-${String(bjResult.getUTCMonth() + 1).padStart(2, '0')}-${String(bjResult.getUTCDate()).padStart(2, '0')}`;
            })()
          : (() => {
              const d = new Date();
              d.setDate(d.getDate() + 30);
              const bjResult = new Date(d.getTime() + 8 * 60 * 60 * 1000);
              return `${bjResult.getUTCFullYear()}-${String(bjResult.getUTCMonth() + 1).padStart(2, '0')}-${String(bjResult.getUTCDate()).padStart(2, '0')}`;
            })());

        setOrder({
          orderNo: lastOrder.order_no,
          planName: `${lastOrder.organization || ''} ${lastOrder.category || ''} ${lastOrder.level_text || ''}`.trim() || '套餐',
          amount: lastOrder.actual_price != null ? Number(lastOrder.actual_price) : Number(lastOrder.price || 0),
          originalPrice: lastOrder.original_price ? Number(lastOrder.original_price) : undefined,
          discountAmount: lastOrder.original_price != null && lastOrder.actual_price != null
            ? Number(lastOrder.original_price) - Number(lastOrder.actual_price)
            : undefined,
          pointsDeducted: lastOrder.points_deducted || 0,
          deductionAmount: lastOrder.deduction_amount ? Number(lastOrder.deduction_amount) : undefined,
          payMethod: lastOrder.pay_method || undefined,
          status: lastOrder.status === 'paid' ? 'success' : lastOrder.status === 'pending' ? 'pending' : 'fail',
          payTime: lastOrder.paid_at,
          expireDate: lastOrder.expire_date || expireDate,
          packageInfo: {
            organization: lastOrder.organization || '',
            category: lastOrder.category || '',
            level_text: lastOrder.level_text || '',
            duration_type: lastOrder.package_type || lastOrder.duration_type || 'month',
          }
        });

        // 注意：站内信通知已在 PackagePurchaseModal 中发送，此处不再重复发送
      } else {
        setErrorMessage('未找到订单信息');
      }
    } catch (err: any) {
      
      setErrorMessage('获取订单详情失败: ' + err.message);
    }
    setLoading(false);
  };

  const sendPaymentSuccessNotification = async (userId: string, order: any, expireDate: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'messages_trigger_scene',
          sceneType: 'order_paid',
          userId: userId,
          variables: {
            order_no: order.order_no,
            amount: String(order.actual_price != null ? order.actual_price : order.price),
          },
        }),
      });
    } catch (err) {
      
    }
  };

  const handleRetry = async () => {
    if (retryCount >= 3) {
      setErrorMessage('已达到最大重试次数，请联系客服');
      return;
    }
    setRetryCount(prev => prev + 1);
    await fetchOrderDetails();
  };

  const handleGoToOrders = () => {
    navigate('/profile?tab=orders');
  };

  const statusConfig = {
    success: {
      icon: CheckCircle,
      title: '支付成功',
      desc: '付费权益已开通',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500/20',
    },
    fail: {
      icon: XCircle,
      title: '支付失败',
      desc: '请重新尝试支付',
      color: 'from-red-500 to-pink-500',
      bgColor: 'bg-red-500/20',
    },
    pending: {
      icon: Clock,
      title: '支付处理中',
      desc: '请稍候查看结果',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-500/20',
    },
  };

  const config = statusConfig[order?.status || status as keyof typeof statusConfig] || statusConfig.success;
  const Icon = config.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/80">加载订单信息...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      <header className="bg-blue-600 text-white py-3">
        <div className="max-w-6xl mx-auto px-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 hover:bg-blue-700 px-2 py-1 rounded">
            <ArrowLeft className="w-4 h-4" />返回
          </button>
          <button onClick={() => navigate('/')} className="flex items-center gap-1 hover:bg-blue-700 px-2 py-1 rounded">
            <Home className="w-4 h-4" />首页
          </button>
        </div>
      </header>
      <div className="flex items-center justify-center p-4 pt-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 shadow-2xl text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className={`w-20 h-20 bg-gradient-to-br ${config.color} rounded-full flex items-center justify-center mx-auto mb-6`}
            >
              <Icon className="w-10 h-10 text-white" />
            </motion.div>

            <h1 className="text-2xl font-bold text-white mb-2">{config.title}</h1>
            <p className="text-blue-200 mb-8">{config.desc}</p>

            {errorMessage && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-6">
                <p className="text-red-200 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {errorMessage}
                </p>
              </div>
            )}

            {order && (
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left space-y-3">
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-blue-200 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />订单号
                  </span>
                  <span className="text-white font-medium font-mono text-sm">{order.orderNo}</span>
                </div>

                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-blue-200 flex items-center gap-2">
                    <Package className="w-4 h-4" />套餐
                  </span>
                  <span className="text-white font-medium flex items-center gap-1">
                    <Crown className="w-4 h-4 text-amber-400" />
                    {order.planName}
                  </span>
                </div>

                {order.packageInfo && (
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-blue-200">有效期</span>
                    <span className="text-white font-medium">
                      {getDurationText(order.packageInfo.duration_type)}
                    </span>
                  </div>
                )}

                {order.originalPrice && order.originalPrice !== order.amount && (
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-blue-200">原价</span>
                    <span className="text-white/60 line-through">¥{order.originalPrice.toFixed(2)}</span>
                  </div>
                )}

                {order.discountAmount && order.discountAmount > 0 && (
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-blue-200">优惠</span>
                    <span className="text-green-400 font-medium">-¥{order.discountAmount.toFixed(2)}</span>
                  </div>
                )}

                {order.pointsDeducted && order.pointsDeducted > 0 && (
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-blue-200 flex items-center gap-2">
                      U币抵扣
                    </span>
                    <span className="text-orange-400 font-medium">
                      {order.pointsDeducted}分 (-¥{(order.deductionAmount || 0).toFixed(2)})
                    </span>
                  </div>
                )}

                {order.payMethod === 'points' && (
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-blue-200">支付方式</span>
                    <span className="text-orange-400 font-medium">U币全额支付</span>
                  </div>
                )}

                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-blue-200">实付金额</span>
                  <span className="text-white font-bold text-lg">
                    {order.payMethod === 'points' ? '¥0.00' : `¥${order.amount.toFixed(2)}`}
                  </span>
                </div>

                {order.payTime && (
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-blue-200 flex items-center gap-2">
                      <Clock className="w-4 h-4" />支付时间
                    </span>
                    <span className="text-white font-medium">{formatDateTime(order.payTime)}</span>
                  </div>
                )}

                {order.expireDate && (
                  <div className="flex justify-between py-2">
                    <span className="text-blue-200 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />到期时间
                    </span>
                    <span className="text-white font-medium">{order.expireDate}</span>
                  </div>
                )}
              </div>
            )}


            {order?.status === 'fail' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/10 rounded-xl p-4 mb-6 border border-red-500/20"
              >
                <p className="text-red-200 text-sm mb-3">支付遇到问题？您可以：</p>
                <ul className="text-red-200/70 text-xs space-y-1 text-left">
                  <li>• 检查网络连接后重试</li>
                  <li>• 更换支付方式</li>
                  <li>• 联系客服获取帮助</li>
                </ul>
                {retryCount < 3 && (
                  <button
                    onClick={handleRetry}
                    className="mt-3 px-4 py-2 bg-red-500/30 hover:bg-red-500/40 text-red-200 rounded-lg transition-colors flex items-center gap-2 mx-auto"
                  >
                    <RefreshCw className="w-4 h-4" />
                    重新查询订单状态
                  </button>
                )}
              </motion.div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => navigate('/exam-categories')}
                className="flex-1 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                返回刷题平台
              </button>
              {order && (
                <button
                  onClick={handleGoToOrders}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  查看订单
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
